import React, { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "config/axiosConfig";
import CommentCard from "components/Comments/Comment";
import CommentInput from "components/Comments/CommentInput";
import ContextCommentPicker from "components/Comments/ContextCommentPicker";
import type { CommentInputContext } from "components/Comments/CommentInput";
import type { CoauthorSuggestion } from "components/Coauthors/CoauthorSelect";
import { uploadEntityMediaDirect } from "util/entityMediaUpload";
import { apiRoutes } from "util/apiRoutes";
import { useCommentsByCommentableQuery } from "hooks/queries/useCommentsQuery";
import { useUser } from "util/UserContext";
import { buildDraftStorageKey } from "util/draftStorage";
import { getTaskUiStrings, resolveTaskLocale } from "i18n/taskUi";
import { getDraftUiStrings, resolveDraftLocale } from "i18n/draftUi";
import type { Comment as CommentEntity } from "util/constants";
import type { PrivacyLevel } from "util/userUtils";
import type { PollDraft } from "types/poll";

interface UserPrinciplesSectionProps {
  userId: number;
}

function buildPollPayload(pollDraft?: PollDraft | null): Record<string, unknown> | undefined {
  if (!pollDraft) {
    return undefined;
  }

  return {
    statement: pollDraft.statement,
    poll_type: pollDraft.poll_type,
    options: pollDraft.options.map((option) => ({
      option_text: option.option_text,
      option_entity_type: option.option_entity_type,
      option_entity_id: option.option_entity_id,
    })),
  };
}

const noopAddReply = (
  _parentId: number,
  _content: string,
  _privacy: PrivacyLevel,
  _coauthors: CoauthorSuggestion[]
) => {};
const noopUpdateComment = (
  _id: number,
  _content: string,
  _privacy: PrivacyLevel,
  _coauthors: CoauthorSuggestion[],
  _addedCoauthors: CoauthorSuggestion[],
  _removedCoauthors: CoauthorSuggestion[]
) => {};
const noopDeleteComment = (_id: number) => {};

const isProfilePrinciple = (comment: CommentEntity): boolean =>
  comment.properties?.user_rel_type === "principle" ||
  comment.properties?.is_user_rel === true;

const UserPrinciplesSection: React.FC<UserPrinciplesSectionProps> = ({ userId }) => {
  const { currentUser, userIsMobile } = useUser();
  const taskUi = getTaskUiStrings(resolveTaskLocale());
  const draftUi = getDraftUiStrings(resolveDraftLocale());
  const [pendingNewPrincipleMedia, setPendingNewPrincipleMedia] = useState<FileList | null>(null);

  const principlesQuery = useCommentsByCommentableQuery("user", userId, { kind: "principle" });
  const principleDraftKey = currentUser
    ? buildDraftStorageKey("draft", "comment", "principle", "new", currentUser.id, "user", userId)
    : null;
  const canCreatePrinciples = currentUser?.id === userId;
  const profilePrinciples = (principlesQuery.data ?? []).filter(isProfilePrinciple);

  const handleCreatePrinciple = async (
    content: string,
    privacy: PrivacyLevel,
    coauthors: CoauthorSuggestion[],
    mediaFiles?: FileList | null,
    pollDraft?: PollDraft | null,
    _context?: CommentInputContext | null
  ) => {
    try {
      const response = await api.post<CommentEntity>(apiRoutes.comments.byCommentable("user", userId), {
        content,
        kind: "principle",
        properties: {
          privacy,
        },
        poll: buildPollPayload(pollDraft),
      });

      await api.post(apiRoutes.votes.comment(response.data.id), { value: 1 });

      if (coauthors.length > 0 && privacy !== "anonymous") {
        try {
          await api.post(apiRoutes.coauthors.request("comment", response.data.id), {
            addedCoauthors: coauthors.map((coauthor) => ({
              id: coauthor.id,
              type: coauthor.type,
            })),
          });
        } catch {
          /* best-effort */
        }
      }

      if (mediaFiles && mediaFiles.length > 0) {
        try {
          await uploadEntityMediaDirect(
            "comment",
            response.data.id,
            Array.from(mediaFiles).map((file, index) => ({
              file,
              order: index + 1,
            }))
          );
        } catch {
          /* best-effort */
        }
      }

      setPendingNewPrincipleMedia(null);
      await principlesQuery.refetch();
      toast.success(taskUi.principleAdded);
    } catch {
      toast.error(taskUi.principleCreateError);
    }
  };

  const handleSavePrincipleDraft = async (
    content: string,
    privacy: PrivacyLevel,
    coauthors: CoauthorSuggestion[],
    pollDraft?: PollDraft | null
  ) => {
    await api.post<CommentEntity>(apiRoutes.comments.byCommentable("user", userId), {
      content,
      kind: "principle",
      status: "draft",
      properties: {
        privacy,
        draft_coauthors: coauthors.map((coauthor) => ({ id: coauthor.id, type: coauthor.type })),
      },
      poll: buildPollPayload(pollDraft),
    });
    toast.success(draftUi.draftSaved);
  };

  const handleAttachExistingPrinciple = async (principle: CommentEntity) => {
    try {
      await api.post(apiRoutes.comments.profilePrinciple(principle.id));
      await principlesQuery.refetch();
      toast.success(taskUi.principleAdded);
    } catch {
      toast.error(taskUi.principleCreateError);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
        {taskUi.principlesSectionSubtitle}
      </p>

      {canCreatePrinciples ? (
        <div className="space-y-3">
          <ContextCommentPicker
            kind="principle"
            // Match SolutionPostForm: surface the caller's own principles
            // first, then infinite-scroll into the global pool of public/
            // anonymous principles authored by anyone. Picked rows are
            // attached via `attachExistingPrincipleToProfile`, which
            // accepts both the caller's own principles and any
            // publicly-reusable principle.
            ownerScope="mixed"
            onPick={(principle) => {
              void handleAttachExistingPrinciple(principle);
            }}
            onCreateNew={() => {
              // The full rich composer is already visible below; focus the
              // user there rather than opening a second inline editor.
              const editor = document.querySelector<HTMLElement>("[contenteditable=true]");
              editor?.focus();
            }}
            excludeCommentIds={profilePrinciples.map((principle) => principle.id)}
          />
          <CommentInput
            action="add"
            onSubmit={async (content, privacy, coauthors, _addedCoauthors, _removedCoauthors, pollDraft, context) => {
              await handleCreatePrinciple(
                content,
                privacy,
                coauthors,
                pendingNewPrincipleMedia,
                pollDraft,
                context
              );
            }}
            buttonText={taskUi.addPrinciple}
            onMediaSelected={(files) => setPendingNewPrincipleMedia(files)}
            onSaveDraft={handleSavePrincipleDraft}
            draftKey={principleDraftKey}
            draftEnabled={Boolean(principleDraftKey)}
            isMobile={userIsMobile}
          />
        </div>
      ) : null}

      {principlesQuery.isLoading ? (
        <div className="flex items-center gap-2 text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
          <Loader2 className="size-4 animate-spin" aria-hidden />
          <span>{taskUi.principlesLoading}</span>
        </div>
      ) : principlesQuery.isError ? (
        <p className="text-sm text-warning-red dark:text-warning-red-dark">
          {taskUi.principlesError}
        </p>
      ) : profilePrinciples.length === 0 ? (
        <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
          {taskUi.principlesEmpty}
        </p>
      ) : (
        <div className="space-y-2">
          {profilePrinciples.map((principle) => (
            <CommentCard
              key={principle.id}
              {...principle}
              depth={0}
              maxDepth={1}
              isAllCollapsed={false}
              isEntitySection={true}
              onAddReply={noopAddReply}
              onUpdateComment={noopUpdateComment}
              onDeleteComment={noopDeleteComment}
              showActions={false}
              showEntityCompactCard={false}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default UserPrinciplesSection;
